import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AccessControlComponent } from './access-control/access-control.component';
import { PermissionsComponent } from './permissions/permissions.component';
import {
  Facility,
  FacilityWithLevels,
  FeatureFlag,
  SavePermissionsPayload,
  UserAccessSavePayload,
} from '../../user-management.model';

@Component({
  selector: 'app-user-access-control',
  imports: [AccessControlComponent, PermissionsComponent],
  templateUrl: './user-access-control.component.html',
  styleUrl: './user-access-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAccessControlComponent implements OnChanges {
  @Input() allFeatureFlags: FeatureFlag[] = [];
  @Input() enabledFeatureFlags: FeatureFlag[] = [];
  @Input() permissions: Facility[] = [];
  @Input() productGroups: FacilityWithLevels[] = [];

  @Output() isDirtyChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<SavePermissionsPayload>();

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  flagsForm: FormGroup = this.fb.group({});
  currentProductGroups: FacilityWithLevels[] = [];
  private originalProductGroups: FacilityWithLevels[] = [];
  private permissionsValid = true;
  formReady = signal<boolean>(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allFeatureFlags'] || changes['enabledFeatureFlags']) {
      this.buildFlagsForm();
    }
    if (changes['productGroups']) {
      this.currentProductGroups = structuredClone(this.productGroups);
      this.originalProductGroups = structuredClone(this.productGroups);
    }
  }

  private buildFlagsForm(): void {
    const enabledIds = new Set(this.enabledFeatureFlags.map((f) => f.id));
    const controls = this.allFeatureFlags.reduce(
      (acc, flag) => {
        acc[flag.id] = this.fb.control(enabledIds.has(flag.id));
        return acc;
      },
      {} as Record<string, ReturnType<FormBuilder['control']>>
    );

    this.flagsForm = this.fb.group(controls);
    this.formReady.set(true);

    this.flagsForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isDirtyChange.emit(this.isDirty());
    });
  }

  private isDirty(): boolean {
    const currentEnabledIds = this.allFeatureFlags.filter((f) => this.flagsForm.value[f.id]).map((f) => f.id);
    const originalIds = this.enabledFeatureFlags.map((f) => f.id);
    return (
      this.permissionsValid &&
      (JSON.stringify(currentEnabledIds) !== JSON.stringify(originalIds) ||
        JSON.stringify(this.currentProductGroups) !== JSON.stringify(this.originalProductGroups))
    );
  }

  onPermissionsChanged(updated: FacilityWithLevels[]): void {
    this.currentProductGroups = updated;
    this.isDirtyChange.emit(this.isDirty());
  }

  onPermissionsValidityChange(valid: boolean): void {
    this.permissionsValid = valid;
    this.isDirtyChange.emit(this.isDirty());
  }

  getPayload(): UserAccessSavePayload {
    const featureFlags = this.allFeatureFlags.filter((f) => this.flagsForm.value[f.id]);
    return {
      featureFlags,
      permissions: this.currentProductGroups,
    };
  }

  triggerSave(): void {
    const permissions = Object.entries(this.flagsForm.value)
      .filter(([, enabled]) => enabled === true)
      .map(([key]) => `featureFlags:${key}`);

    for (const fac of this.currentProductGroups) {
      for (const product of fac.products) {
        for (const type of ['Technical', 'Pricing'] as const) {
          const level = product.currentLevels[type];
          if (level > 0) {
            permissions.push(`productPermissions:${product.id}:${fac.facility}:${type}:${level}`);
          }
        }
      }
    }

    this.save.emit({ Permissions: permissions });
  }
}
